// SPDX-License-Identifier: AGPL-3.0-or-later

// LOCAL-ONLY: the bumper's side of Bump (bump-plan.md sections 1 and 3): which channels offer it, the request, the
// "Bumped" toast or the refusal's own string, and the action disabled while it runs and for 2 s after (one shared
// state, so the header button and the menus agree). Exclude from upstream sync.

import {BumpStrings} from '@app/features/bump/BumpStrings';
import ChannelBumps, {BUMP_BUSY_AFTER_MS} from '@app/features/bump/state/ChannelBumps';
import type {Channel} from '@app/features/channel/models/Channel';
import Guilds from '@app/features/guild/state/Guilds';
import Permission from '@app/features/permissions/state/Permission';
import {http} from '@app/features/platform/transport/RestTransport';
import {Logger} from '@app/features/platform/utils/AppLogger';
import {failureCode} from '@app/features/platform/utils/ResponseInspection';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {ChannelTypes, Permissions} from '@fluxer/constants/src/ChannelConstants';

const logger = new Logger('Bump');

const REFUSALS: Record<string, string> = {
	BUMP_RATE_LIMITED: BumpStrings.rateLimited,
	BUMP_TOO_MANY_TARGETS: BumpStrings.tooManyTargets,
	BUMP_NO_TARGETS: BumpStrings.noTargets,
	BUMP_NO_MESSAGES: BumpStrings.noMessages,
	BUMP_NOT_AVAILABLE: BumpStrings.notAvailable,
};

/** DMs and group DMs, and server text channels the user can send in (not while previewing a server). */
export function canBump(channel: Pick<Channel, 'id' | 'type' | 'guildId'> | null | undefined): boolean {
	if (channel == null) return false;
	if (channel.type === ChannelTypes.DM || channel.type === ChannelTypes.GROUP_DM) return true;
	if (channel.type !== ChannelTypes.GUILD_TEXT || channel.guildId == null) return false;
	if (Guilds.getGuild(channel.guildId)?.visiting === true) return false;
	return Permission.can(Permissions.SEND_MESSAGES, {channelId: channel.id, guildId: channel.guildId});
}

export function refusalText(code: string | undefined): string {
	return (code != null ? REFUSALS[code] : undefined) ?? BumpStrings.failed;
}

export async function bump(channelId: string): Promise<boolean> {
	if (ChannelBumps.isBusy(channelId)) return false;
	ChannelBumps.setBusy(channelId, true);
	try {
		await http.post(`/channels/${channelId}/bump`);
		ToastCommands.createToast({type: 'success', children: BumpStrings.bumped});
		return true;
	} catch (error) {
		logger.warn('Bump failed:', error);
		ToastCommands.createToast({type: 'error', children: refusalText(failureCode(error))});
		return false;
	} finally {
		setTimeout(() => ChannelBumps.setBusy(channelId, false), BUMP_BUSY_AFTER_MS);
	}
}
